/**
 * The frame every report renders inside.
 *
 * Spec: .claude/specs/reports-v2-shell-and-people-analytics.md (Package 2).
 *
 * Takes a report name, renders the controls (period, comparison, grouping,
 * export), fetches the envelope and draws whatever is in it — figures,
 * breakdowns, series, notes. It does not know which report it is showing; a new
 * report is a backend query, not a new page.
 */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";

import {
  downloadReportPdf, getReport, getReportRows,
  ReportBreakdownRow, ReportEnvelope, ReportFigure, ReportName, ReportQuery,
} from "../api";
import { useToast } from "../context/ToastContext";

const NAVY = "#1e3a8a";
const SLATE = "#64748b";
const LINE = "#e2e8f0";
const RED = "#dc2626";
const GREEN = "#16a34a";

const PERIODS: { key: string; label: string }[] = [
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_quarter", label: "This quarter" },
  { key: "this_year", label: "This year" },
  { key: "last_year", label: "Last year" },
  { key: "custom", label: "Custom range" },
];

const COMPARISONS: { key: ReportQuery["compare"]; label: string }[] = [
  { key: "none", label: "No comparison" },
  { key: "previous", label: "vs previous period" },
  { key: "last_year", label: "vs last year" },
];

const GROUP_LABELS: Record<string, string> = {
  department: "Department", role: "Role", manager: "Manager", leave_type: "Leave type",
};

function fmtValue(v: number | null | undefined, format: string, symbol?: string | null) {
  if (v === null || v === undefined) return "—";
  if (format === "money") return `${symbol ?? ""}${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (format === "percent") return `${v}%`;
  if (format === "days") return `${v}`;
  return v.toLocaleString();
}

/** Colour a delta by whether the movement is good for *this* metric. */
function deltaColour(f: ReportFigure) {
  if (!f.direction || f.direction === "flat" || f.good === "neutral") return SLATE;
  const improving = (f.direction === "up" && f.good === "up") || (f.direction === "down" && f.good === "down");
  return improving ? GREEN : RED;
}

function DeltaLabel({ f }: { f: ReportFigure }) {
  if (f.previous === null || f.previous === undefined) return null;
  // AC9 — a delta of zero is "no change", never "+0%" or an arrow.
  if (f.direction === "flat") {
    return <div style={{ fontSize: 12, color: SLATE, marginTop: 4 }}>No change</div>;
  }
  const arrow = f.direction === "up" ? "▲" : "▼";
  const magnitude = fmtValue(f.delta === null ? null : Math.abs(f.delta), f.format, f.currency_symbol);
  // A percentage against a zero base is undefined, so it reads "new" instead.
  const pct = f.delta_pct === null ? "new" : `${Math.abs(f.delta_pct)}%`;
  return (
    <div style={{ fontSize: 12, color: deltaColour(f), marginTop: 4, fontWeight: 600 }}>
      {arrow} {magnitude} ({pct})
      <span style={{ color: SLATE, fontWeight: 400 }}>
        {" "}from {fmtValue(f.previous, f.format, f.currency_symbol)}
      </span>
    </div>
  );
}

const FigureCard: React.FC<{ f: ReportFigure }> = ({ f }) => {
  return (
    <div style={{ background: "white", border: `1px solid ${LINE}`, borderRadius: 12, padding: "18px 20px" }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: NAVY }}>
        {fmtValue(f.value, f.format, f.currency_symbol)}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", marginTop: 2 }}>{f.label}</div>
      {f.sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{f.sub}</div>}
      <DeltaLabel f={f} />
    </div>
  );
};

const BreakdownCard: React.FC<{
  bd: { label: string; group_by: string; rows: ReportBreakdownRow[] };
  onDrill: (bucket: string) => void;
}> = ({ bd, onDrill }) => {
  const max = Math.max(...bd.rows.map(r => r.value || 0), 1);
  return (
    <div style={{ background: "white", border: `1px solid ${LINE}`, borderRadius: 12, padding: 20 }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{bd.label}</h3>
      {!bd.rows.length && <div style={{ color: "#94a3b8", fontSize: 13 }}>No data for this period</div>}
      {bd.rows.map(r => {
        const sym = r.currency_symbol;
        const pct = Math.round(((r.value || 0) / max) * 100);
        return (
          <button
            key={r.key}
            type="button"
            onClick={() => onDrill(r.key)}
            title={`Show the rows behind ${r.label}`}
            style={{
              display: "block", width: "100%", textAlign: "left", background: "none",
              border: "none", padding: "6px 4px", cursor: "pointer", borderRadius: 6,
              minHeight: 44,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 500 }}>
                {r.color && (
                  <span style={{
                    display: "inline-block", width: 9, height: 9, borderRadius: "50%",
                    background: r.color, marginRight: 7,
                  }} />
                )}
                {r.label}
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: NAVY, whiteSpace: "nowrap" }}>
                {sym ? fmtValue(r.value, "money", sym) : (r.value ?? 0).toLocaleString()}
                {r.previous !== null && r.previous !== undefined && (
                  <span style={{ color: SLATE, fontWeight: 400, fontSize: 12 }}>
                    {" "}(was {sym ? fmtValue(r.previous, "money", sym) : r.previous.toLocaleString()})
                  </span>
                )}
              </span>
            </div>
            <div style={{ height: 7, background: LINE, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: NAVY, borderRadius: 4 }} />
            </div>
          </button>
        );
      })}
    </div>
  );
};

function SeriesCard({ s }: { s: ReportEnvelope["series"][number] }) {
  const max = Math.max(...s.points.map(p => p.total), 1);
  const money = (n: number) => `${s.currency_symbol}${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <div style={{ background: "white", border: `1px solid ${LINE}`, borderRadius: 12, padding: 20 }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
        Monthly{s.currency ? ` — ${s.currency}` : ""}
      </h3>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, minHeight: 190, overflowX: "auto" }}>
        {s.points.map(p => {
          // A zero-filled month is a flat grey stub, never a short navy bar —
          // "no pay run" must not read as "a small pay run".
          const empty = !p.total;
          const h = Math.max(8, Math.round((p.total / max) * 165));
          return (
            <div key={p.month} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "0 0 auto", minWidth: 54 }}>
              <div style={{ fontSize: 10, color: SLATE, marginBottom: 4 }}>{empty ? "—" : money(p.total)}</div>
              <div
                title={empty ? `${p.month}: no pay run` : `${p.month}: ${money(p.total)}`}
                style={{
                  width: 38, height: empty ? 3 : h,
                  background: empty ? LINE : `linear-gradient(to top, ${NAVY}, #3b82f6)`,
                  borderRadius: "4px 4px 0 0",
                }}
              />
              <div style={{ fontSize: 11, color: SLATE, marginTop: 6 }}>{p.month}</div>
              <div style={{ fontSize: 10, color: "#94a3b8" }}>{empty ? "—" : `${p.headcount} emp`}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DrillModal({
  report, query, bucket, onClose,
}: {
  report: ReportName; query: ReportQuery; bucket: string; onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["report-rows", report, query, bucket],
    queryFn: () => getReportRows(report, query, bucket),
  });
  // `any` here is deliberate, and is the point of this modal: every report's
  // drill-through returns a different row shape — employees, payslips,
  // attendance days, leave balances — and the table renders whatever columns
  // arrive rather than knowing them in advance. A union type would have to be
  // widened for each new report, which is exactly the coupling the shell exists
  // to avoid.
  const rows: any[] = data?.rows ?? [];
  const columns = rows.length ? Object.keys(rows[0]).filter(k => k !== "id") : [];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "white", borderRadius: 12, maxWidth: 900, width: "100%",
          maxHeight: "80vh", display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${LINE}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{bucket}</h3>
            <div style={{ fontSize: 12, color: SLATE, marginTop: 2 }}>
              {isLoading ? "Loading…" : `${rows.length} row${rows.length === 1 ? "" : "s"}`}
            </div>
          </div>
          <button type="button" onClick={onClose} style={{
            background: "white", border: `1px solid #cbd5e1`, borderRadius: 6,
            padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, minHeight: 44,
          }}>Close</button>
        </div>
        <div className="table-wrap" style={{ overflow: "auto", padding: 4 }}>
          {!isLoading && !rows.length && (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
              Nothing behind this one.
            </div>
          )}
          {!!rows.length && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {columns.map(c => (
                    <th key={c} style={{
                      textAlign: "left", padding: "10px 12px", borderBottom: `1px solid ${LINE}`,
                      color: SLATE, fontWeight: 600, whiteSpace: "nowrap",
                    }}>
                      {c.replace(/_/g, " ")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id ?? i}>
                    {columns.map(c => (
                      <td key={c} style={{ padding: "9px 12px", borderBottom: `1px solid #f1f5f9` }}>
                        {r[c] === null || r[c] === undefined || r[c] === "" ? "—" : String(r[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReportShell({
  report, title, subtitle, tabs,
}: {
  report: ReportName; title: string; subtitle?: string;
  /**
   * The page's report picker, rendered directly under the header. Multi-report
   * pages used to stack their own TabPillBar *above* ReportShell, which put a
   * naked row of pills at the top of the page and pushed the only <h1> below
   * them — the page read as having no title at all. The tab strip belongs to
   * the report, so the shell owns where it sits.
   */
  tabs?: React.ReactNode;
}) {
  const { toast } = useToast();
  const [period, setPeriod] = useState("this_month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [compare, setCompare] = useState<ReportQuery["compare"]>("none");
  const [groupBy, setGroupBy] = useState<string | null>(null);
  // Which salary basis values unused leave is a statutory question that differs
  // by country and is unresolved for this workspace (spec Open Question 3), so
  // it is a control rather than a constant buried in the valuation.
  const [basis, setBasis] = useState<"basic" | "gross">("basic");
  const hasBasis = report === "leave-liability";
  const [drill, setDrill] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // AC7 — a backwards custom range must not fire a request at all.
  const rangeInvalid = period === "custom" && !!start && !!end && end < start;
  const rangeIncomplete = period === "custom" && (!start || !end);

  const query: ReportQuery = {
    period,
    start: period === "custom" ? start : null,
    end: period === "custom" ? end : null,
    compare,
    group_by: groupBy,
    ...(hasBasis ? { basis } : {}),
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["report", report, query],
    queryFn: () => getReport(report, query),
    enabled: !rangeInvalid && !rangeIncomplete,
  });

  async function onExport() {
    setExporting(true);
    try {
      await downloadReportPdf(report, query);
    } catch {
      toast("Could not generate the PDF. Please try again.", "error");
    } finally {
      setExporting(false);
    }
  }

  const groupings = data?.supports_group_by ?? [];
  const control: React.CSSProperties = {
    padding: "9px 11px", border: "1px solid #cbd5e1", borderRadius: 6,
    fontSize: 13, fontWeight: 600, color: "#0f172a", background: "white", minHeight: 44,
  };

  return (
    <div>
      {/* The house `.page-header` block, matching every other page. #426's
          "one title per page" removal was reverted upstream in #460, so pages
          carry their own header again alongside the chrome title — and a report
          with no title at all would be the odd one out. Plain <h1>, no
          className, which is the current idiom (see TimesheetPage). */}
      <div className="page-header">
        <div>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>

      {tabs}

      {/* Controls wrap rather than scroll, so 375px stacks instead of clipping. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 8 }}>
        <select aria-label="Period" value={period} onChange={e => setPeriod(e.target.value)} style={control}>
          {PERIODS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>

        {period === "custom" && (
          <>
            <input aria-label="Start date" type="date" value={start} onChange={e => setStart(e.target.value)} style={control} />
            <span style={{ color: SLATE, fontSize: 13 }}>to</span>
            <input aria-label="End date" type="date" value={end} onChange={e => setEnd(e.target.value)} style={control} />
          </>
        )}

        <select aria-label="Comparison" value={compare} onChange={e => setCompare(e.target.value as ReportQuery["compare"])} style={control}>
          {COMPARISONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>

        {groupings.length > 1 && (
          <select
            aria-label="Group by"
            value={groupBy ?? data?.group_by ?? groupings[0]}
            onChange={e => setGroupBy(e.target.value)}
            style={control}
          >
            {groupings.map(g => <option key={g} value={g}>Group by {GROUP_LABELS[g] ?? g}</option>)}
          </select>
        )}

        {hasBasis && (
          <select
            aria-label="Salary basis"
            value={basis}
            onChange={e => setBasis(e.target.value as "basic" | "gross")}
            style={control}
          >
            <option value="basic">Value at basic salary</option>
            <option value="gross">Value at basic + earnings</option>
          </select>
        )}

        <button
          type="button"
          onClick={onExport}
          disabled={exporting || !data || !!data?.unavailable_module}
          style={{
            ...control, display: "inline-flex", alignItems: "center", gap: 7,
            background: NAVY, color: "white", border: "none",
            cursor: exporting || !data ? "default" : "pointer",
            opacity: exporting || !data || data?.unavailable_module ? 0.55 : 1,
          }}
        >
          <Download size={14} />
          {exporting ? "Preparing…" : "Export PDF"}
        </button>
      </div>

      {rangeInvalid && (
        <div style={{ color: RED, fontSize: 13, marginBottom: 14, fontWeight: 600 }}>
          The end date cannot be before the start date.
        </div>
      )}
      {rangeIncomplete && !rangeInvalid && (
        <div style={{ color: SLATE, fontSize: 13, marginBottom: 14 }}>
          Pick a start and end date to run this report.
        </div>
      )}

      {data && !rangeInvalid && !rangeIncomplete && (
        <div style={{ fontSize: 12, color: SLATE, marginBottom: 20 }}>
          {data.period.label} ({data.period.start} to {data.period.end})
          {data.comparison && ` · compared with ${data.comparison.label}`}
        </div>
      )}

      {isLoading && <div style={{ padding: 60, textAlign: "center", color: "#94a3b8" }}>Loading…</div>}

      {isError && (
        <div style={{ padding: 40, textAlign: "center", color: RED, fontSize: 13 }}>
          {(error as any)?.response?.data?.detail ?? "This report could not be loaded."}
        </div>
      )}

      {data?.unavailable_module && (
        <div style={{ background: "#f8fafc", border: `1px solid ${LINE}`, borderRadius: 12, padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
            This report needs the {data.unavailable_module} module
          </div>
          <div style={{ fontSize: 13, color: SLATE, marginTop: 6 }}>
            It isn't part of your plan yet, so there is nothing to report on here.
          </div>
        </div>
      )}

      {data && !data.unavailable_module && (
        <>
          {/* AC14 — an empty period says so rather than drawing a zeroed chart
              that reads as a real result. */}
          {!data.figures.length && !data.breakdowns.length && !data.series.length ? (
            <div style={{ padding: 70, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
              No data for this period.
            </div>
          ) : (
            <>
              {!!data.figures.length && (
                <div style={{
                  display: "grid", gap: 14, marginBottom: 22,
                  gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
                }}>
                  {data.figures.map(f => <FigureCard key={f.key} f={f} />)}
                </div>
              )}

              {data.series.map(s => (
                <div key={s.currency} style={{ marginBottom: 22 }}><SeriesCard s={s} /></div>
              ))}

              <div style={{
                display: "grid", gap: 20,
                gridTemplateColumns: data.breakdowns.length > 1 ? "repeat(auto-fit, minmax(320px, 1fr))" : "1fr",
              }}>
                {data.breakdowns.map(bd => (
                  <BreakdownCard key={bd.label} bd={bd} onDrill={setDrill} />
                ))}
              </div>
            </>
          )}

          {!!data.notes.length && (
            <div style={{ marginTop: 22, paddingTop: 16, borderTop: `1px solid ${LINE}` }}>
              {data.notes.map(n => (
                <div key={n} style={{ fontSize: 12, color: SLATE, marginBottom: 5 }}>• {n}</div>
              ))}
            </div>
          )}
        </>
      )}

      {drill && (
        <DrillModal report={report} query={query} bucket={drill} onClose={() => setDrill(null)} />
      )}
    </div>
  );
}
